import { SVG_ENUM } from "enums";
import moment from "moment";
import React, { useState } from "react";
import { AnalyzeStatusBadge, SVG } from "shared";
import { AnalyzeType } from "types/AnalyzeTypes";
import JSZip from "jszip";

interface AnalyzeTableProps {
  analyze: AnalyzeType | null;
  isAdmin?: boolean;
}

const AnalyzeTable: React.FC<AnalyzeTableProps> = ({ analyze, isAdmin }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const hasFiles =
    analyze && analyze.analysisFiles && analyze.analysisFiles.length > 0;

  const handleDownloadFiles = async () => {
    if (!hasFiles || !analyze?.analysisFiles || isDownloading) return;

    setIsDownloading(true);

    try {
      const zip = new JSZip();

      const filePromises = analyze.analysisFiles.map(async (file, index) => {
        try {
          const response = await fetch(file.url);
          if (!response.ok) throw new Error(`Failed to fetch ${file.name}`);
          const blob = await response.blob();

          const fileName = file.name || `plik-${index + 1}`;
          return { fileName, blob };
        } catch (error) {
          console.error(`Błąd pobierania pliku ${file.name}:`, error);
          return null;
        }
      });

      const files = await Promise.all(filePromises);

      files.forEach((file) => {
        if (file) {
          zip.file(file.fileName, file.blob);
        }
      });

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `analiza-${
        analyze.companyName || "pliki"
      }-${moment().format("YYYY-MM-DD")}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Błąd tworzenia archiwum:", error);
      if (analyze?.analysisFiles) {
        analyze.analysisFiles.forEach((file, index) => {
          setTimeout(() => {
            const link = document.createElement("a");
            link.href = file.url;
            link.download = file.name || `plik-${index + 1}`;
            link.click();
          }, index * 500);
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <table className="side-modal__table">
      <tr>
        <td className="side-modal__table-label">Nazwa spółki:</td>
        <td className="side-modal__table-cell">
          {analyze?.companyName || "-"}
        </td>
      </tr>
      <tr>
        <td className="side-modal__table-label">NIP spółki:</td>
        <td className="side-modal__table-cell">{analyze?.nip || "-"}</td>
      </tr>
      <tr>
        <td className="side-modal__table-label">Data zlecenia:</td>
        <td className="side-modal__table-cell">
          {moment(analyze?.createdAt).format("DD/MM/YYYY") || "-"}
        </td>
      </tr>
      {isAdmin && (
        <tr>
          <td className="side-modal__table-label">Zlecający:</td>
          <td className="side-modal__table-cell">
            {analyze?.user?.name || "-"}
          </td>
        </tr>
      )}

      <tr>
        <td className="side-modal__table-label">Termin wykonania:</td>
        <td className="side-modal__table-cell">
          <div className="analyze-info-modal__deadline-row">
            <span>{moment(analyze?.deadlineAt).format("DD/MM/YYYY") || "-"}</span>
            {analyze?.status === "pending" ? (
              <span className="companies-table__unconfirmed-badge">
                Niepotwierdzony
              </span>
            ) : null}
          </div>
        </td>
      </tr>

      {isAdmin && (
        <>
          <tr>
            <td className="side-modal__table-label">Załączone pliki:</td>
            <td className="side-modal__table-cell">
              <div
                className={`side-modal__download-files ${
                  hasFiles ? "side-modal__download-files--has-files" : ""
                } ${
                  isDownloading ? "side-modal__download-files--downloading" : ""
                }`}
                onClick={() => handleDownloadFiles()}
                style={{
                  opacity: isDownloading ? 0.6 : 1,
                  pointerEvents: isDownloading ? "none" : "auto",
                }}
              >
                {hasFiles ? (
                  <>
                    {isDownloading
                      ? "Przygotowywanie ZIP..."
                      : `Pobierz wszystkie (${analyze!.analysisFiles!.length})`}
                    <SVG type={SVG_ENUM.FILE} />
                  </>
                ) : (
                  0
                )}
              </div>
            </td>
          </tr>
        </>
      )}
      <tr>
        <td className="side-modal__table-label">O spółce:</td>
        <td className="side-modal__table-cell">
          {analyze?.description && analyze.description !== "undefined"
            ? analyze.description
            : "-"}
        </td>
      </tr>
      {analyze?.status && (
        <tr>
          <td className="side-modal__table-label">Status:</td>
          <td className="side-modal__table-cell">
            <AnalyzeStatusBadge
              className="analyze-status--onlyDesktopVersion"
              status={analyze?.status}
            />
          </td>
        </tr>
      )}
      {analyze?.explanation && (
        <tr>
          <td className="side-modal__table-label">Wyjaśnienie:</td>
          <td className="side-modal__table-cell">
            {analyze?.explanation || "-"}
          </td>
        </tr>
      )}
    </table>
  );
};

export default AnalyzeTable;
